/**
 * BookBlom — global.js
 * Shared utilities and site-wide behaviour, written as Custom Elements so
 * they work on server-rendered Liquid and re-initialise automatically when
 * the Theme Editor re-renders a section.
 *
 *   BB.ui          scroll lock, focus trap, toasts, money formatting
 *   DrawerElement   base class for drawers (menu, cart)
 *   <menu-drawer> <sticky-header> <announcement-bar> <carousel-slider>
 *   <quantity-input> <newsletter-form> <localization-form> <share-button>
 *   Reveal-on-scroll + parallax, footer accordions, nav dropdowns.
 */
(function () {
  'use strict';

  var BB = (window.BB = window.BB || {});
  var theme = window.theme || {};
  var ui = (BB.ui = BB.ui || {});

  /* ---------------------------------------------------------------------- */
  /* Utilities                                                               */
  /* ---------------------------------------------------------------------- */
  ui.reducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  ui.debounce = function (fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  };

  ui.escapeHtml = function (value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  };

  /** Format cents with the shop's money format (e.g. "{{amount_no_decimals}} kr"). */
  ui.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || theme.moneyFormat || '{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      var parts = (number / 100).toFixed(precision).split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var match = format.match(placeholder);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, value);
  };

  /** Parse an HTML string and return the inner HTML of `selector`. */
  ui.parseSection = function (html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  };

  var lockCount = 0;
  ui.lockScroll = function () {
    if (lockCount++ > 0) return;
    var sb = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = sb > 0 ? sb + 'px' : '';
    document.body.classList.add('is-scroll-locked');
  };
  ui.unlockScroll = function () {
    if (--lockCount > 0) return;
    lockCount = 0;
    document.body.classList.remove('is-scroll-locked');
    document.body.style.paddingRight = '';
  };

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /** Keep Tab focus inside `container`. Returns a release function. */
  ui.trapFocus = function (container, initial) {
    function focusables() {
      return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
    }
    function onKey(e) {
      if (e.key !== 'Tab') return;
      var els = focusables();
      if (!els.length) return;
      var first = els[0];
      var last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKey);
    setTimeout(function () {
      var target = initial || focusables()[0];
      if (target) target.focus({ preventScroll: true });
    }, 60);
    return function () { document.removeEventListener('keydown', onKey); };
  };

  /**
   * Toast notification.
   * ui.toast({ title, text, imageUrl, icon: 'check'|'error', action: { label, onClick }, duration })
   */
  ui.clearToasts = function () {
    document.querySelectorAll('[data-toast-region] .toast').forEach(function (t) { t.remove(); });
  };
  ui.toast = function (o) {
    var region = document.querySelector('[data-toast-region]');
    if (!region) return function () {};
    var el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', o.icon === 'error' ? 'alert' : 'status');
    var media = o.imageUrl
      ? '<span class="toast__media media"><img src="' + ui.escapeHtml(o.imageUrl) + '" alt="" width="44" height="44"></span>'
      : '<span class="toast__icon' + (o.icon === 'error' ? ' toast__icon--error' : '') + '" aria-hidden="true">' +
        (o.icon === 'error'
          ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 8v5M12 16.5v.01"/></svg>'
          : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>') +
        '</span>';
    el.innerHTML =
      media +
      '<div class="toast__body"><p class="toast__title">' + ui.escapeHtml(o.title) + '</p>' +
      (o.text ? '<p class="toast__text">' + ui.escapeHtml(o.text) + '</p>' : '') + '</div>' +
      (o.action ? '<button type="button" class="toast__action">' + ui.escapeHtml(o.action.label) + '</button>' : '');

    function dismiss() {
      if (el.classList.contains('is-leaving')) return;
      el.classList.add('is-leaving');
      setTimeout(function () { el.remove(); }, 320);
    }
    if (o.action) {
      el.querySelector('.toast__action').addEventListener('click', function () { o.action.onClick(); dismiss(); });
    }
    ui.clearToasts();
    region.appendChild(el);
    var timer = setTimeout(dismiss, o.duration || 4500);
    el.addEventListener('mouseenter', function () { clearTimeout(timer); });
    el.addEventListener('mouseleave', function () { timer = setTimeout(dismiss, 1800); });
    return dismiss;
  };

  /* ---------------------------------------------------------------------- */
  /* Drawers                                                                 */
  /* ---------------------------------------------------------------------- */
  class DrawerElement extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]')) this.close();
      });
      this.onKeydown = (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); };
      document.addEventListener('keydown', this.onKeydown);
    }
    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.isOpen) { ui.unlockScroll(); if (this.releaseFocus) this.releaseFocus(); }
      this._bound = false;
    }
    get isOpen() { return this.classList.contains('is-open'); }
    triggers() { return document.querySelectorAll('[data-drawer-open="' + this.id + '"]'); }
    open(trigger) {
      if (this.isOpen) return;
      document.querySelectorAll('.drawer.is-open').forEach((d) => { if (d !== this && d.close) d.close({ silent: true }); });
      this.returnFocus = trigger || document.activeElement;
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => this.classList.add('is-open'));
      this.triggers().forEach((t) => t.setAttribute('aria-expanded', 'true'));
      ui.lockScroll();
      ui.clearToasts();
      this.releaseFocus = ui.trapFocus(this.querySelector('.drawer__panel'), this.initialFocus && this.initialFocus());
    }
    close(opts) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      this.triggers().forEach((t) => t.setAttribute('aria-expanded', 'false'));
      ui.unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      if (!(opts && opts.silent) && this.returnFocus && this.returnFocus.focus) this.returnFocus.focus({ preventScroll: true });
    }
    initialFocus() { return this.querySelector('.drawer__header [data-drawer-close]'); }
  }
  BB.DrawerElement = DrawerElement;

  if (!customElements.get('menu-drawer')) {
    customElements.define('menu-drawer', class extends DrawerElement {
      connectedCallback() {
        super.connectedCallback();
        this.mq = window.matchMedia('(min-width: 1100px)');
        this.onMq = () => {
          // Close when the menu button disappears (layouts without a desktop menu button)
          const toggle = document.querySelector('.site-header__menu-toggle');
          if (this.mq.matches && (!toggle || toggle.offsetParent === null)) this.close({ silent: true });
        };
        if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMq);
      }
      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.mq && this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMq);
      }
    });
  }

  // Any [data-drawer-open="ID"] trigger opens that drawer (cart icon, menu button…)
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (drawer && typeof drawer.open === 'function') {
      e.preventDefault();
      drawer.open(trigger);
    }
  });
  // Keyboard: role="button" links also open on Space
  document.addEventListener('keydown', (e) => {
    if (e.key !== ' ') return;
    const trigger = e.target.closest && e.target.closest('a[data-drawer-open][role="button"]');
    if (trigger) { e.preventDefault(); trigger.click(); }
  });

  /* ---------------------------------------------------------------------- */
  /* Header                                                                  */
  /* ---------------------------------------------------------------------- */
  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', class extends HTMLElement {
      connectedCallback() {
        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.classList.toggle('is-scrolled', window.scrollY > 8);
            this.ticking = false;
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();

        // Desktop dropdowns: one open at a time, close on outside click / Escape
        this.dropdowns = this.querySelectorAll('[data-nav-dropdown]');
        this.dropdowns.forEach((d) => {
          d.addEventListener('toggle', () => {
            if (d.open) this.dropdowns.forEach((o) => { if (o !== d) o.removeAttribute('open'); });
          });
        });
        this.onDocClick = (e) => {
          this.dropdowns.forEach((d) => { if (d.open && !d.contains(e.target)) d.removeAttribute('open'); });
        };
        this.onKey = (e) => {
          if (e.key !== 'Escape') return;
          this.dropdowns.forEach((d) => {
            if (d.open) { d.removeAttribute('open'); d.querySelector('summary').focus(); }
          });
        };
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKey);
      }
      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKey);
      }
    });
  }

  if (!customElements.get('announcement-bar')) {
    customElements.define('announcement-bar', class extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.announcement-bar__item')).filter((el) => !el.hidden);
        this.index = 0;
        this.mq = window.matchMedia('(max-width: 899px)');
        this.delay = (Number(this.dataset.autoplay) || 4) * 1000;
        this.paused = false;
        ['mouseenter', 'focusin'].forEach((ev) => this.addEventListener(ev, () => { this.paused = true; }));
        ['mouseleave', 'focusout'].forEach((ev) => this.addEventListener(ev, () => { this.paused = false; }));
        if (this.items.length > 1 && !ui.reducedMotion()) {
          this.timer = setInterval(() => this.next(), this.delay);
        }
        this.onMq = () => this.update();
        if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMq);
        this.update();
      }
      disconnectedCallback() {
        clearInterval(this.timer);
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMq);
      }
      next() {
        if (this.paused || !this.mq.matches || document.hidden || this.editorLocked) return;
        this.index = (this.index + 1) % this.items.length;
        this.update();
      }
      show(index) {
        this.index = index;
        this.editorLocked = true;
        this.update();
      }
      update() {
        this.items.forEach((el, i) => {
          const active = i === this.index;
          el.classList.toggle('is-active', active);
          if (this.mq.matches) el.setAttribute('aria-hidden', String(!active));
          else el.removeAttribute('aria-hidden');
        });
      }
    });
  }

  /**
   * <countdown-timer data-end="epoch-ms"> — counts down to a sale end set by the
   * merchant. When the time has passed the item hides itself (no fake urgency).
   */
  if (!customElements.get('countdown-timer')) {
    customElements.define('countdown-timer', class extends HTMLElement {
      connectedCallback() {
        this.end = Number(this.dataset.end);
        this.parts = {
          h: this.querySelector('[data-hours]'),
          m: this.querySelector('[data-minutes]'),
          s: this.querySelector('[data-seconds]')
        };
        if (!this.end) return this.finish();
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }
      disconnectedCallback() { clearInterval(this.timer); }
      digits(n) {
        return String(n).padStart(2, '0').split('').map((d) => '<span class="countdown__digit">' + d + '</span>').join('');
      }
      tick() {
        const left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
        if (left <= 0) return this.finish();
        const h = Math.floor(left / 3600);
        const m = Math.floor((left % 3600) / 60);
        const s = left % 60;
        if (this.parts.h) this.parts.h.innerHTML = this.digits(h);
        if (this.parts.m) this.parts.m.innerHTML = this.digits(m);
        if (this.parts.s) this.parts.s.innerHTML = this.digits(s);
      }
      finish() {
        clearInterval(this.timer);
        // Campaign over: remove the timer, keep the message only if it has text.
        const item = this.closest('.announcement-bar__item');
        this.hidden = true;
        if (item && !item.querySelector('.countdown__text')) item.hidden = true;
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Carousel (testimonials)                                                 */
  /* ---------------------------------------------------------------------- */
  if (!customElements.get('carousel-slider')) {
    customElements.define('carousel-slider', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;
        this.items = Array.from(this.track.children);
        this.prev = this.querySelector('[data-carousel-prev]');
        this.next = this.querySelector('[data-carousel-next]');
        this.dots = this.querySelector('[data-carousel-dots]');
        if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
        if (this.next) this.next.addEventListener('click', () => this.go(1));
        this.track.addEventListener('keydown', (e) => {
          if (e.target !== this.track) return;
          if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1); }
        });
        this.track.addEventListener('scroll', () => {
          cancelAnimationFrame(this.raf);
          this.raf = requestAnimationFrame(() => this.update());
        }, { passive: true });
        if (this.dots) this.dots.innerHTML = this.items.map(() => '<span class="carousel__dot"></span>').join('');
        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => this.update());
          this.ro.observe(this.track);
        }
        this.update();
      }
      disconnectedCallback() { if (this.ro) this.ro.disconnect(); }
      step() {
        if (this.items.length < 2) return this.track.clientWidth;
        return this.items[1].offsetLeft - this.items[0].offsetLeft;
      }
      go(dir) {
        this.track.scrollBy({ left: dir * this.step(), behavior: ui.reducedMotion() ? 'auto' : 'smooth' });
      }
      scrollToItem(item) {
        this.track.scrollTo({ left: item.offsetLeft - this.items[0].offsetLeft, behavior: 'auto' });
      }
      update() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const x = this.track.scrollLeft;
        this.classList.toggle('is-static', max <= 4);
        if (this.prev) this.prev.disabled = x <= 4;
        if (this.next) this.next.disabled = x >= max - 4;
        if (this.dots) {
          let idx = Math.round(x / (this.step() || 1));
          if (x >= max - 4) idx = this.items.length - 1;
          Array.from(this.dots.children).forEach((d, i) => d.classList.toggle('is-active', i === idx));
        }
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Forms                                                                   */
  /* ---------------------------------------------------------------------- */
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        if (!this.input) return;
        this.addEventListener('click', (e) => {
          const b = e.target.closest('[data-step]');
          if (!b) return;
          e.preventDefault();
          const step = Number(this.input.step) || 1;
          const min = this.input.min !== '' ? Number(this.input.min) : 0;
          const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
          const next = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + step * Number(b.dataset.step)));
          if (next === Number(this.input.value)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  if (!customElements.get('newsletter-form')) {
    customElements.define('newsletter-form', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.input = this.querySelector('input[type="email"]');
        this.consent = this.querySelector('input[type="checkbox"]');
        this.msg = this.querySelector('.newsletter-form__message');
        this.form.addEventListener('submit', (e) => {
          const value = this.input.value.trim();
          const strings = (window.theme && window.theme.strings) || {};
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
            e.preventDefault();
            return this.error(strings.emailInvalid || this.input.validationMessage || 'Please enter a valid email address.', this.input);
          }
          if (this.consent && !this.consent.checked) {
            e.preventDefault();
            return this.error(strings.consentRequired || 'Please tick the box to agree to receive emails.', this.consent);
          }
          // Valid: let Shopify's native customer form submit.
        });
        [this.input, this.consent].forEach((el) => el && el.addEventListener('input', () => this.clear()));
      }
      clear() {
        this.msg.textContent = '';
        this.msg.classList.remove('is-error');
        this.input.removeAttribute('aria-invalid');
        if (this.consent) this.consent.closest('.checkbox').classList.remove('is-invalid');
      }
      error(text, field) {
        this.msg.textContent = text;
        this.msg.classList.remove('is-success');
        this.msg.classList.add('is-error');
        if (field === this.input) this.input.setAttribute('aria-invalid', 'true');
        else if (field) field.closest('.checkbox').classList.add('is-invalid');
        field.focus();
      }
    });
  }

  if (!customElements.get('localization-form')) {
    customElements.define('localization-form', class extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('[data-autosubmit]').forEach((select) => {
          select.addEventListener('change', () => this.querySelector('form').submit());
        });
      }
    });
  }

  if (!customElements.get('share-button')) {
    customElements.define('share-button', class extends HTMLElement {
      connectedCallback() {
        const btn = this.querySelector('[data-share]');
        const status = this.querySelector('[data-share-status]');
        if (!btn) return;
        btn.addEventListener('click', () => {
          const url = this.dataset.url || window.location.href;
          if (navigator.share) {
            navigator.share({ title: this.dataset.title, url: url }).catch(() => {});
            return;
          }
          if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(() => {
              status.textContent = (window.theme && window.theme.strings.copied) || 'Link copied';
              setTimeout(() => { status.textContent = ''; }, 2500);
            }).catch(() => {});
          }
        });
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Footer accordions (mobile) — always open from 768px                     */
  /* ---------------------------------------------------------------------- */
  function initFooterAccordions() {
    const mq = window.matchMedia('(min-width: 768px)');
    const sync = () => {
      document.querySelectorAll('.footer-accordion').forEach((d) => {
        if (mq.matches) d.setAttribute('open', '');
        else if (!d.dataset.userOpened) d.removeAttribute('open');
      });
    };
    document.querySelectorAll('.footer-accordion').forEach((d) => {
      if (d.dataset.accordionBound) return;
      d.dataset.accordionBound = '1';
      d.querySelector('summary').addEventListener('click', (e) => {
        if (mq.matches) { e.preventDefault(); return; }
        d.dataset.userOpened = d.open ? '' : '1';
      });
    });
    if (!initFooterAccordions.bound) {
      initFooterAccordions.bound = true;
      if (mq.addEventListener) mq.addEventListener('change', sync);
    }
    sync();
  }

  /* ---------------------------------------------------------------------- */
  /* Motion: reveal on scroll + gentle parallax                             */
  /* ---------------------------------------------------------------------- */
  const motion = (BB.motion = {});
  const animationsOn = () => !(theme.animations && theme.animations.reveal === false) && !ui.reducedMotion() && !(window.Shopify && Shopify.designMode);

  motion.initReveal = function (root) {
    const els = (root || document).querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!('IntersectionObserver' in window) || !animationsOn()) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    if (!motion.io) {
      motion.io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          motion.io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    els.forEach((el) => motion.io.observe(el));
  };

  motion.initParallax = function () {
    if (ui.reducedMotion() || (theme.animations && theme.animations.parallax === false)) return;
    let ticking = false;
    const update = () => {
      const vh = window.innerHeight;
      document.querySelectorAll('[data-parallax]').forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const speed = Number(el.getAttribute('data-parallax')) || 0.05;
        const progress = (r.top + r.height / 2 - vh / 2) / vh;
        el.style.transform = 'translate3d(0,' + (progress * speed * -100).toFixed(2) + '%,0)';
      });
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
    motion.updateParallax = update;
  };

  /* ---------------------------------------------------------------------- */
  /* Boot + Theme Editor integration                                          */
  /* ---------------------------------------------------------------------- */
  function start() {
    initFooterAccordions();
    motion.initReveal();
    motion.initParallax();
    document.documentElement.classList.add('is-ready');
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', (e) => {
    motion.initReveal(e.target);
    initFooterAccordions();
    if (motion.updateParallax) motion.updateParallax();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    const bar = block.closest('announcement-bar');
    if (bar && typeof bar.show === 'function') bar.show(Number(block.dataset.index) || 0);
    const carousel = block.closest('carousel-slider');
    if (carousel && typeof carousel.scrollToItem === 'function') carousel.scrollToItem(block);
  });
  document.addEventListener('shopify:section:select', (e) => {
    motion.initReveal(e.target);
  });
})();
